import React, { useState, useContext, useEffect } from "react";
import { withStyles } from "@mui/styles";
import { Checkbox, FormControl } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import { WIDGET_COLORS_ARRAY } from "components/color/colors";

import { ErrorContext } from "services/Utils";
import { Input } from "@mui/material";

var Chart = require("chart.js");

const ERROR_MESSAGES = {
  missingSeriesType: "Please enter a series type",
  missingOutputUnit: "Please enter an output unit",
  missingStates: "Please enter at least 2 states",
  missingValues:
    "Please enter a lower bound, upper bound, and minimum duration for each state",
  missingDisplay: "Please enter a default dispaly state for each state",
  missingColor: "Please enter a color for each state",
  missingNoDataColor: "Please enter a color for the Missing Data setting",
  // missingNoDataMinDuration:
  //   "Please enter a minimum duration for the Missing Data setting",
  missingLabel: "Please enter a label for each state",
};

// const SmallerInputLabel = withStyles((theme) => ({
//   root: { fontSize: "0.75rem" },
// }))(InputLabel);

const StyledTableCell = withStyles((theme) => ({
  head: {
    paddingLeft: 12,
    paddingRight: 12,
    textAlign: "center",
  },
  body: {
    paddingLeft: 12,
    paddingRight: 12,
    borderTop: "none",
    textAlign: "center",
  },
}))(TableCell);

const StateEntryLabelCell = ({
  id,
  label,
  statesSeriesType,
  rowIndex,
  onChangeState,
  colSpan,
}) => (
  <StyledTableCell
    key={"row-" + rowIndex + "-label"}
    colSpan={colSpan}
    style={{ paddingBottom: "1.25rem", paddingLeft: 0 }}
  >
    <FormControl
      variant="standard"
      style={{
        width: statesSeriesType === "full" ? "auto" : "100%",
      }}>
      <TextField
        variant="standard"
        aria-label="label"
        id={`label-${id}`}
        name="label"
        label={
          "State Label: " +
          (statesSeriesType !== "full"
            ? rowIndex < 2
              ? "(for state " + rowIndex + ")"
              : "(unused)"
            : "")
        }
        value={label}
        onChange={onChangeState(id)}
        required
        style={{
          margin: 6,
        }} />
    </FormControl>
  </StyledTableCell>
);

const StatesTagProperties = (props) => {
  const [errors, setErrors] = useState([]);

  const errorContext = useContext(ErrorContext);

  const { tagId, tagProperties, handleTagPropChange, readOnly } = props;
  const cvtChecked = tagProperties?.da?.states;
  const readOnlyOrChecked = readOnly || cvtChecked;

  function togglePropDA() {
    const current = tagProperties?.da || {};
    delete current['states'];
    handleTagPropChange(tagId, "da", cvtChecked ? current : {...current, states: {}});
  }
  
  useEffect(() => {
    const validation = ({
      states,
      statesNoDataColor,
      // statesNoDataMinDuration,
      statesSeriesType,
    }) => {
      // need to check for errors, but only if config begun, e.g. at least one
      // state property is populated
      const newErrors = [];
      if (
        states ||
        statesNoDataColor ||
        // statesNoDataMinDuration ||
        statesSeriesType
      ) {
        if (!statesSeriesType) {
          newErrors.push(ERROR_MESSAGES.missingSeriesType);
        }
        if (statesSeriesType === "full") {
          if (
            states?.some(
              (state) =>
                !state.lowerBound || !state.upperBound || !state.minDuration
            )
          ) {
            newErrors.push(ERROR_MESSAGES.missingValues);
          }
          if (states?.some((state) => !state.color || state.color === "")) {
            newErrors.push(ERROR_MESSAGES.missingColor);
          }
        } else if (statesSeriesType === "edges") {
          if ((states ?? []).length < 2) {
            newErrors.push(ERROR_MESSAGES.missingStates);
          }
          if (states?.some((state) => !state.label)) {
            newErrors.push(ERROR_MESSAGES.missingLabel);
          }
          if (states?.some((state) => !state.color)) {
            newErrors.push(ERROR_MESSAGES.missingColor);
          }
        }
        if (!statesNoDataColor) {
          newErrors.push(ERROR_MESSAGES.missingNoDataColor);
        }
        // if (!statesNoDataMinDuration) {
        //   newErrors.push(ERROR_MESSAGES.missingNoDataMinDuration);
        // }
      }
      setErrors(newErrors);
      errorContext.setErrorById(tagId, newErrors.length > 0);
    };

    validation(tagProperties);
  }, [tagProperties, tagId, errorContext]);

  const addState = () => {
    const states = tagProperties.states ?? [];
    const newId =
      states.reduce((maxId, { id }) => (id > maxId ? id : maxId), 0) + 1;
    const newStates = [
      ...states,
      {
        id: newId,
        label: "",
        lowerBound: "",
        upperBound: "",
        minDuration: "",
        color: "",
        display: "on",
      },
    ];
    handleTagPropChange(tagId, "states", newStates);
  };

  const deleteState = (id) => {
    const newStates = (tagProperties.states ?? {}).filter(
      (state) => state.id !== id
    );
    handleTagPropChange(tagId, "states", newStates);
  };

  const onChangeState = (id) => (event) => {
    const { name, value } = event.target;

    const newStates = tagProperties.states.map((state) =>
      state.id !== id ? state : { ...state, [name]: value }
    );
    handleTagPropChange(tagId, "states", newStates);
  };

  return (
    <div style={{ width: "100%" }}>
      <FormControl variant="standard" style={{ width: "100%", margin: 0, marginBottom: 0 }}>
        <div style={{display:"flex", alignItems:"center", gap: "8px"}}>
        <FormLabel component="legend">
          Series type
          <span> 
            { tagProperties.statesSeriesType ? " " : "(please select)" }
          </span>
        </FormLabel>
        </div>
      </FormControl>
      <FormControl variant="standard" style={{ width: "100%" }}>
        <RadioGroup
          aria-label="seriesType"
          name="seriesType"
          value={tagProperties.statesSeriesType ?? ""}
          onChange={(event) =>
            handleTagPropChange(tagId, "statesSeriesType", event.target.value)
          }
          row
          disabled={readOnlyOrChecked}
        >
          <FormControlLabel
            value="full"
            control={<Radio />}
            label="Continuous"
            disabled={readOnlyOrChecked}
          />
          <FormControlLabel
            value="edges"
            control={<Radio />}
            label="Discrete"
            disabled={readOnlyOrChecked}
          />
        </RadioGroup>
      </FormControl>
      <Grid container spacing={3}>
        <Grid item>
          <FormControl variant="standard" style={{ width: "33%", minWidth: 300 }}>
            <InputLabel id="noDataColor-label">
              Color for Missing/Unclassified Data
            </InputLabel>
            <Select
              variant="standard"
              aria-label="noDataColor"
              id="noDataColor"
              name="noDataColor"
              labelId="noDataColor-label"
              value={tagProperties.statesNoDataColor ?? ""}
              onChange={(event) =>
                handleTagPropChange(
                  tagId,
                  "statesNoDataColor",
                  event.target.value
                )
              }
              required
              disabled={readOnly || !tagProperties.statesSeriesType}>
              {WIDGET_COLORS_ARRAY.map((color, index) => {
                const alphaColor = Chart.helpers.color(color).rgbString();

                return (
                  <MenuItem key={"no-data-color-" + index} value={alphaColor}>
                    <div
                      style={{
                        height: 16,
                        width: 20,
                        backgroundColor: alphaColor,
                        border: "1px solid darkgray",
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <FormLabel
            component="legend"
            style={{ fontSize: 14, fontStyle: "italic" }}
          >
            Min. Duration for Missing/Unclassified Data (in seconds)
          </FormLabel>
          <div>
            <FormControl variant="standard">
              <RadioGroup
                aria-label="seriesType"
                name="seriesType"
                value={tagProperties.statesNoDataMinDuration?.type ?? "auto"}
                onChange={(event) =>
                  handleTagPropChange(tagId, "statesNoDataMinDuration", {
                    ...tagProperties.statesNoDataMinDuration,
                    type: event.target.value,
                  })
                }
                row
                disabled={readOnlyOrChecked}
              >
                <FormControlLabel
                  value="auto"
                  control={<Radio />}
                  label="Auto"
                  disabled={readOnlyOrChecked}
                />
                <FormControlLabel
                  value="set"
                  control={<Radio />}
                  label="Set"
                  disabled={readOnlyOrChecked}
                />
              </RadioGroup>
            </FormControl>
            {tagProperties.statesNoDataMinDuration?.type === "set" && (
              <FormControl variant="standard">
                <Input
                  id="label-no-data-min-duration"
                  aria-label="noDataMinDuration"
                  name="noDataMinDuration"
                  // label="Min. Duration for Missing/Unclassified Data (in seconds)"
                  value={tagProperties.statesNoDataMinDuration?.value ?? 0}
                  onChange={(event) =>
                    handleTagPropChange(tagId, "statesNoDataMinDuration", {
                      ...tagProperties.statesNoDataMinDuration,
                      value: event.target.value,
                    })
                  }
                  required
                  type="number"
                />
              </FormControl>
            )}
          </div>
        </Grid>
      </Grid>
      <div style={{ width: "100%", marginTop: 32 }}>
        <Table>
          {tagProperties.states?.length > 0 && (
            <TableHead>
              <TableRow>
                <StyledTableCell
                  colSpan={1}
                  style={{ paddingLeft: 0 }}
                ></StyledTableCell>
                {tagProperties.statesSeriesType === "full" && (
                  <>
                    <StyledTableCell style={{ paddingLeft: 0 }}>
                      Lower Bound
                    </StyledTableCell>
                    <StyledTableCell>Upper Bound</StyledTableCell>
                    <StyledTableCell>
                      Min. Duration (in seconds)
                    </StyledTableCell>
                  </>
                )}
                <StyledTableCell>Color</StyledTableCell>
                {/* <StyledTableCell>Display on Load</StyledTableCell> */}
                <StyledTableCell style={{ paddingRight: 0 }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {tagProperties.states?.map((state, rowIndex) => {
              const { id, label, lowerBound, upperBound, minDuration, color } =
                state;

              return (
                <React.Fragment key={"row-" + rowIndex + "-fragment"}>
                  <TableRow key={"row-" + rowIndex + "-bottom"}>
                    {tagProperties.statesSeriesType === "full" ? (
                      <>
                        <StateEntryLabelCell
                          {...{ id, label, rowIndex, onChangeState }}
                          statesSeriesType={tagProperties.statesSeriesType}
                          // colSpan={5}
                        />
                        <StyledTableCell
                          key={"row-" + rowIndex + "-lowerBound"}
                          style={{ paddingLeft: 0 }}
                        >
                          <TextField
                            variant="standard"
                            aria-label="lowerBound"
                            id={`lowerBound-${id}`}
                            name="lowerBound"
                            value={lowerBound}
                            onChange={onChangeState(id)}
                            type="number"
                            style={{
                              margin: 6,
                            }}
                            disabled={readOnlyOrChecked} />
                        </StyledTableCell>
                        <StyledTableCell
                          key={"row-" + rowIndex + "-upperBound"}
                        >
                          <TextField
                            variant="standard"
                            aria-label="upperBound"
                            id="upperBound"
                            name="upperBound"
                            value={upperBound}
                            onChange={onChangeState(id)}
                            type="number"
                            style={{
                              margin: 6,
                            }}
                            disabled={readOnlyOrChecked} />
                        </StyledTableCell>
                        <StyledTableCell
                          key={"row-" + rowIndex + "-minDuration"}
                        >
                          <TextField
                            variant="standard"
                            aria-label="minDuration"
                            id={"minDuration-" + id}
                            name="minDuration"
                            value={minDuration}
                            onChange={onChangeState(id)}
                            type="number"
                            style={{
                              margin: 6,
                            }}
                            disabled={readOnlyOrChecked} />
                        </StyledTableCell>
                      </>
                    ) : (
                      <StateEntryLabelCell
                        {...{ id, label, rowIndex, onChangeState }}
                        statesSeriesType={tagProperties.statesSeriesType}
                        // colSpan={1}
                      />
                    )}
                    <StyledTableCell key={"row-" + rowIndex + "-color"}>
                      <FormControl variant="standard">
                        <Select
                          variant="standard"
                          aria-label="color"
                          id={`color-${id}`}
                          name="color"
                          value={color || ""}
                          onChange={onChangeState(id)}
                          required
                          style={{
                            margin: 6,
                          }}
                          disabled={readOnlyOrChecked}>
                          {WIDGET_COLORS_ARRAY.map((color, index) => {
                            const alphaColor = Chart.helpers
                              .color(color)
                              .rgbString();

                            return (
                              <MenuItem
                                key={`row-${id}-color-${index}`}
                                value={alphaColor}
                              >
                                <div
                                  style={{
                                    height: 16,
                                    width: 20,
                                    backgroundColor: alphaColor,
                                    border: "1px solid darkgray",
                                  }}
                                />
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell
                      key={"row-" + rowIndex + "-cancel"}
                      style={{ paddingRight: 0 }}
                    >
                      <IconButton onClick={() => deleteState(id)} size="large">
                        <CancelIcon />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            <TableRow>
              <StyledTableCell
                colSpan={tagProperties.statesSeriesType !== "full" ? 4 : 6}
                style={{ textAlign: "left" }}
              >
                <Button
                  onClick={addState}
                  startIcon={<AddCircleIcon />}
                  color="primary"
                  disabled={readOnlyOrChecked || !tagProperties.statesSeriesType}
                >
                  Add State
                </Button>
              </StyledTableCell>
            </TableRow>
          </TableBody>
        </Table>
        {
          tagProperties.statesSeriesType &&
            <FormLabel component="legend">
              <Checkbox
                size="small" checked={ cvtChecked }
                onChange={(e) => { togglePropDA(); e.stopPropagation() } }
                onClick={(e) => { e.stopPropagation() } }
              />
              Calculated Value Tags (CVT) for States Enabled <i>(be sure you know what you are doing before enabling!)</i>
            </FormLabel>
        }
        {errors.length > 0 &&
          errors.map((msg, index) => (
            <p key={"err-" + index} style={{ color: "red", fontSize: "10px" }}>
              {msg}
            </p>
          ))}
      </div>
    </div>
  );
};

export default StatesTagProperties;
